import React from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './TutorialMessageBox.css';

/**
 * Reusable tutorial message box with image, text, optional multichoice selector, and buttons.
 * @param {Object} props
 * @param {string} props.image - Path to image (e.g. /images/tutorial_welcome.png)
 * @param {string} props.text - Main text content
 * @param {Array<{key: string, value: string}>} [props.choices] - Optional multichoice options
 * @param {string} [props.choiceLabelPrefix] - Translation key prefix for choice labels (e.g. 'battleOverview.tutorial'). Default: 'welcome.strategyPreference'
 * @param {Function} [props.onSelect] - Called when user selects a choice (for strategy selection)
 * @param {Function} props.onContinue - Called when user clicks Continue
 * @param {Function} [props.onCancel] - Called when user clicks Cancel
 * @param {boolean} [props.showCancel] - Whether to show Cancel button
 * @param {boolean} [props.disabled] - Disable choice buttons (e.g. while submitting)
 */
function TutorialMessageBox({ image, text, choices, choiceLabelPrefix = 'welcome.strategyPreference', onSelect, onContinue, onCancel, showCancel = false, disabled = false }) {
  const { t } = useTranslation();

  return (
    <div className="tutorial-wrapper">
      <div className="tutorial-message-box">
        {image && (
          <div className="tutorial-image-column">
            <div className="tutorial-image-container">
              <img src={image} alt="" className="tutorial-image" />
            </div>
          </div>
        )}
        <div className="tutorial-content-column">
        <div className="tutorial-text">{text}</div>
        {choices && choices.length > 0 && (
          <div className="tutorial-choices">
            {choices.map(({ key, value }) => (
              <button
                key={key}
                className="tutorial-choice-button"
                onClick={() => {
                  if (!disabled && onSelect) {
                    playSoundEffect('/sounds/click.mp3');
                    onSelect(value);
                  }
                }}
                disabled={disabled}
              >
                {t(`${choiceLabelPrefix}.${key}`)}
              </button>
            ))}
          </div>
        )}
        <div className="tutorial-buttons">
          {!choices?.length && (
            <button
              className="tutorial-continue-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                onContinue();
              }}
            >
              {t('welcome.tutorial.continue')}
            </button>
          )}
          {showCancel && (
            <button
              className="tutorial-cancel-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                if (onCancel) onCancel();
              }}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialMessageBox;
